import React, { useState } from 'react';

import {
  Box,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import ContentCard from '../../../components/ContentCard';
import SearchField from '../../../components/SearchField';
import StudentCard from '../../../components/StudentCard';
import StudentListItem from '../../../components/StudentListItem';
import {
  useUpdateExamMarkingRequestMutation,
} from '../../../services/exams';
import { useGetStudentQuery, useGetStudentsQuery } from '../../../services/students';
import useError from '../../../utils/react/hooks/useError';

interface RequestStudentCardProps {
  requestId: string,
  studentId?: string,
}

const RequestStudentCard = ({ requestId, studentId }: RequestStudentCardProps) => {
  const [searchStr, setSearchStr] = useState('');

  const { data: student } = useGetStudentQuery({
    studentId: studentId || '',
  }, { skip: !studentId });

  const { data: searchedStudents, isFetching: isSearchedStudentsLoading } = useGetStudentsQuery({
    search: searchStr,
  }, { skip: searchStr === '' });

  const [updateMarkingRequest, {
    isError: isUpdateMarkingRequestError,
    error: updateMarkingRequestError,
  }] = useUpdateExamMarkingRequestMutation();

  useError(isUpdateMarkingRequestError, updateMarkingRequestError);

  if (studentId && !student) {
    return (
      <ContentCard title="Student" variant="outlined">
        <Skeleton variant="rectangular" height={120} />
      </ContentCard>
    );
  }

  return (
    <ContentCard title="Student" variant="outlined">
      <Stack direction="column" spacing={2}>
        <SearchField
          type="dropdown"
          placeholder="Search students..."
          options={searchedStudents?.data || []}
          getOptionKey={(s) => s.studentId}
          getOptionLabel={(s) => s.name}
          onSearch={(search) => setSearchStr(search)}
          loading={isSearchedStudentsLoading}
          onSelect={(s) => {
            updateMarkingRequest({
              requestId,
              newAttributes: {
                student: s.studentId,
              },
            });
          }}
          renderOption={(props, s) => (
            <StudentListItem
              student={s}
              {...props}
              key={s.id}
            />
          )}
          clearOnSelect
        />
        {studentId && student?.data ? (
          <ContentCard variant="outlined">
            <StudentCard
              student={student.data}
              onClose={() => {
                updateMarkingRequest({
                  requestId,
                  newAttributes: {
                    student: '',
                  },
                });
              }}
              showState={false}
            />
          </ContentCard>
        ) : (
          <ContentCard variant="outlined">
            <Box padding={1}>
              <Typography variant="overline">
                No student selected
              </Typography>
            </Box>
          </ContentCard>
        )}
      </Stack>
    </ContentCard>
  );
};

export default RequestStudentCard;
